import React, { memo, useEffect, useRef, useState } from "react";
import { Grid, TextField, MenuItem, ClickAwayListener, Popper } from "@mui/material";
import { isValue, type } from "../../../../../utils/constants/constants";
import { FIELD_TYPE, VALUES, VISIBILITY_HIDDEN } from "../../../../../utils/constants/htmlConstants";
import { useSelector } from "react-redux";
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import FlightIcon from '@mui/icons-material/Flight';

const CustomSearchField = ({ data, labelwid, handleChange, setDisableButton, setRoute }) => {
    const { id, name, placeholder, options, disabled, selectedVal } = data
    const shop = useSelector((state) => state.addShop);
    const form3Details = shop?.form3Options;

    const anchorElRef = useRef(null);
    const [selected, setSelected] = useState(id === "flight" ? selectedVal : form3Details?.timeZone);
    const [openPopper, setOpenPopper] = useState("");
    const [filteredOptions, setFilteredOptions] = useState(options);
    const [startIndex, setStartIndex] = useState(0);
    const [visibleOptions, setVisibleOptions] = useState([]); // Only render visible options
    const numVisibleOptions = 100; // Number of options to display at a time
    const { airPorts } = useSelector((state) => state?.airports);

    const allGroupedOptions = React.useMemo(() => {
        if (!airPorts || airPorts.length === 0) return [];
        const cityMap = new Map();
        const noCity = [];
        airPorts.forEach(a => {
            if (a.cityiata) {
                if (!cityMap.has(a.cityiata)) {
                    cityMap.set(a.cityiata, []);
                }
                cityMap.get(a.cityiata).push(a);
            } else {
                noCity.push(a);
            }
        });
        
        let groupedOptions = [];
        let indexCounter = 0;
        cityMap.forEach((cityAirports, cityiata) => {
            const cityName = cityAirports[0]?.cityname;
            const cityNameWithoutSpaces = cityName?.replace(/ /g, '') || "";
            const skipCityHeader = cityAirports.length === 1 && cityAirports[0].airportiata === cityiata;
            
            if (!skipCityHeader) {
                groupedOptions.push({
                    isCity: true, isGrouped: false,
                    label: `${cityiata} ${cityName} All airports`,
                    displayTitle: `${cityiata} ${cityName} All airports`,
                    displaySubtitle: cityName,
                    value: `${cityNameWithoutSpaces} - ${cityiata}`,
                    searchString: `${cityiata} ${cityName}`.toLowerCase(),
                    airportiata: "",
                    cityiata: cityiata?.toLowerCase() || "",
                    cityname: cityName?.toLowerCase() || "",
                    originalIndex: indexCounter++
                });
            }
            
            cityAirports.forEach(ca => {
                const caCityNameWithoutSpaces = ca.cityname?.replace(/ /g, '') || "";
                groupedOptions.push({
                    isCity: false, isGrouped: !skipCityHeader,
                    label: `${ca.airportiata} ${ca.cityname} , ${ca.cityiata} ${ca.airportname}`,
                    displayTitle: `${ca.airportiata} ${ca.airportname}`,
                    displaySubtitle: "", value: `${caCityNameWithoutSpaces} - ${ca.airportiata}`,
                    searchString: `${ca.cityiata || ""} ${ca.airportiata} ${ca.cityname} ${ca.airportname} ${ca.cityname?.replace(/ /g, '')} - ${ca.airportiata}`.toLowerCase(),
                    airportiata: ca.airportiata?.toLowerCase() || "",
                    cityiata: ca.cityiata?.toLowerCase() || "",
                    cityname: ca.cityname?.toLowerCase() || "",
                    originalIndex: indexCounter++,
                    rawCityName: ca.cityname || "",
                    rawCityIata: ca.cityiata || "",
                    rawAirportIata: ca.airportiata || "",
                    rawAirportName: ca.airportname || ""
                });
            });
        });
        noCity.forEach(a => {
            const aCityNameWithoutSpaces = a.cityname?.replace(/ /g, '') || "";
            groupedOptions.push({
                isCity: false, isGrouped: false,
                label: `${a.airportiata} ${a.cityname} , ${isValue?.emptyString || ""} - ${a.airportname}`,
                displayTitle: `${a.airportiata} ${a.airportname}`,
                displaySubtitle: a.cityname, value: `${aCityNameWithoutSpaces} - ${a.airportiata}`,
                searchString: `${a.cityiata || ""} ${a.airportiata} ${a.cityname} ${a.airportname} ${a.cityname?.replace(/ /g, '')} - ${a.airportiata}`.toLowerCase(),
                airportiata: a.airportiata?.toLowerCase() || "",
                cityiata: a.cityiata?.toLowerCase() || "",
                cityname: a.cityname?.toLowerCase() || "",
                originalIndex: indexCounter++,
                rawCityName: a.cityname || "",
                rawCityIata: a.cityiata || "",
                rawAirportIata: a.airportiata || "",
                rawAirportName: a.airportname || ""
            });
        });
        return groupedOptions;
    }, [airPorts]);
    useEffect(() => {
        if (selectedVal !== undefined) {
            setSelected(id === "flight" ? selectedVal : form3Details?.timeZone);
        }
        if (id === "flight" && selectedVal) {
            setRoute((prev) => ({ ...prev, [name]: selectedVal }))
        }
        // eslint-disable-next-line
    }, [selectedVal, form3Details?.timeZone, id]);

    useEffect(() => {
        // Close the popper when clicking outside the component
        const handleClickOutside = (event) => {
            if (
                anchorElRef.current &&
                !anchorElRef.current.contains(event.target)
            ) {
                // setOpenPopper(false);
            }
        };

        document.addEventListener("click", handleClickOutside);
        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        // Update the visible options based on startIndex and numVisibleOptions
        setVisibleOptions(
            filteredOptions?.slice(startIndex, startIndex + numVisibleOptions)
        );
    }, [startIndex, filteredOptions]);

    useEffect(() => {
        filterOptions(selected || "");
        // eslint-disable-next-line
    }, [allGroupedOptions, options, id]);

    const handleTextFieldChange = (event) => {
        const { name, value } = event.target;
        setSelected(value);
        setOpenPopper(name);
        filterOptions(value); // Filter options when the input value changes
        if ((name === "flyfrom" && !value) || (name === "flyto" && !value)) {
            setDisableButton(true)
            handleChange({ value: "" }, name)
        }
        if (name === "flyfrom" || name === "flyto") {
            setRoute((prev) => ({ ...prev, [name]: value }))
        }
    };

    // Handle options for filter section for Search and select field
    const filterOptions = (inputValue) => {
        if (!inputValue) {
            setFilteredOptions(id === "flight" ? allGroupedOptions : options);
            setStartIndex(0);
            return;
        }

        if (id === "flight") {
            const lowerInput = inputValue.toLowerCase();
            const matchedOptions = [];
            const matchedCities = new Set();
            const cityBestScores = {};

            // Pass 1: Identify all matches and calculate initial scores
            for (let i = 0; i < allGroupedOptions.length; i++) {
                const opt = allGroupedOptions[i];
                let matched = false;
                
                // Reset score
                opt.score = 10;

                // Priority 1: Airport IATA
                if (opt.airportiata === lowerInput) { opt.score = 1; matched = true; }
                else if (opt.airportiata.startsWith(lowerInput)) { opt.score = 2; matched = true; }
                else if (opt.airportiata.includes(lowerInput)) { opt.score = 3; matched = true; }
                // Priority 2: City IATA
                else if (opt.cityiata === lowerInput) { opt.score = 4; matched = true; }
                else if (opt.cityiata.startsWith(lowerInput)) { opt.score = 5; matched = true; }
                else if (opt.cityiata.includes(lowerInput)) { opt.score = 6; matched = true; }
                // Priority 3: City Name
                else if (opt.cityname === lowerInput) { opt.score = 7; matched = true; }
                else if (opt.cityname.startsWith(lowerInput)) { opt.score = 8; matched = true; }
                else if (opt.cityname.includes(lowerInput)) { opt.score = 9; matched = true; }
                // Priority 4: General Match (Airport Name, etc.)
                else if (opt.searchString.includes(lowerInput)) { opt.score = 10; matched = true; }

                if (matched) {
                    opt.initialScore = opt.score; // Save original score for intra-group sorting
                    matchedOptions.push(opt);
                    if (opt.isCity) {
                        matchedCities.add(opt.cityiata);
                    }
                    
                    // Track best score for this city group
                    if (opt.cityiata) {
                        if (cityBestScores[opt.cityiata] === undefined || opt.score < cityBestScores[opt.cityiata]) {
                            cityBestScores[opt.cityiata] = opt.score;
                        }
                    }
                }
            }

            // Pass 2: Force inclusion of sibling airports if their City Header was matched
            for (let i = 0; i < allGroupedOptions.length; i++) {
                const opt = allGroupedOptions[i];
                if (!opt.isCity && matchedCities.has(opt.cityiata)) {
                    if (!matchedOptions.includes(opt)) {
                        opt.score = 10; // Default score
                        opt.initialScore = 10;
                        matchedOptions.push(opt);
                    }
                }
            }

            // Pass 3: Unify scores within city groups so they stay grouped during sorting
            matchedOptions.forEach(opt => {
                if (opt.cityiata && cityBestScores[opt.cityiata] !== undefined) {
                    opt.score = cityBestScores[opt.cityiata];
                }
            });

            // Pass 4: Sort options by unified score, then by City Group, then City Header first, then initial score, then original index
            matchedOptions.sort((a, b) => {
                // 1. Group by unified score (City Group level)
                if (a.score !== b.score) return a.score - b.score;
                
                // 2. Group by City so different cities with the same score don't mix
                const aCity = a.cityiata || "none";
                const bCity = b.cityiata || "none";
                
                if (aCity !== bCity || aCity === "none") {
                    // Different cities, or ungrouped airports. Maintain their original array order.
                    // Because original array is grouped by city sequentially, this perfectly keeps city blocks intact.
                    return a.originalIndex - b.originalIndex;
                }
                
                // 3. We are GUARANTEED to be comparing two items within the EXACT SAME City Group
                // City Headers always come first within their group
                if (a.isCity !== b.isCity) return a.isCity ? -1 : 1;
                
                // 4. Exactly matched airports come before forced-in sibling airports
                if (a.initialScore !== b.initialScore) return a.initialScore - b.initialScore;
                
                // 5. Alphabetical fallback
                return a.originalIndex - b.originalIndex;
            });

            setFilteredOptions(matchedOptions.slice(0, 100));
            setStartIndex(0);
        } else {
            const filtered = options?.length ? options.filter((option) =>
                option.label.toString().toLowerCase().includes(inputValue.toLowerCase())
            ) : [];
            setFilteredOptions(filtered);
        }

        setStartIndex(0); // Reset the visible optionsName index when filtering
    };

    const handleClickAway = () => {
        setOpenPopper(false);
    }

    return (
        <>
            <Grid container columns={{ xs: 12, sm: 12, md: 12 }} >
                <Grid item xs={(!!labelwid && name === "timeZone") ? 5 : 12} className={(!!labelwid && name === "timeZone") ? "timedelivery" : ""}>
                    <TextField
                        disabled={disabled}
                        inputRef={anchorElRef}
                        autoComplete="off"
                        type={FIELD_TYPE.TEXT}
                        value={selected || VALUES.EMPTY_STRING}
                        placeholder={placeholder}
                        id={id}
                        name={name}
                        className={"dropdownIcon"}
                        InputLabelProps={{
                            style: { ...VISIBILITY_HIDDEN },
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            // Do not automatically open the dropdown if the field contains a fully completed selection.
                            // Let the user edit (backspace) to trigger the dropdown.
                            let isFullySelected = false;
                            if (id === "flight" && selected) {
                                isFullySelected = allGroupedOptions.some(opt => opt.value === selected);
                            }
                            if (!isFullySelected) {
                                setOpenPopper(name);
                            }
                        }}
                        onChange={handleTextFieldChange}
                    />

                    <ClickAwayListener onClickAway={handleClickAway} >
                        <Popper className="menus-container"
                            open={Boolean(selected) && openPopper === name && (id === "flight" || !/\s/.test(selected))}
                            anchorEl={anchorElRef.current}
                            placement="bottom-start"
                            sx={{ maxHeight: "10rem", overflow: "scroll", zIndex: 3 }}
                        >
                            {visibleOptions?.length > 0 ? (
                                visibleOptions.map((item, idx) => (
                                    <MenuItem
                                        key={idx}
                                        value={item}
                                        onClick={(e) => {
                                            handleChange(item, name);
                                            setSelected(name === "flyfrom" || name === "flyto" ? item.value : item?.label);
                                            if (name === "flyfrom" || name === "flyto") {
                                                setRoute((prev) => ({ ...prev, [name]: item.value }));
                                            }

                                            setOpenPopper(false);
                                        }}
                                        sx={{
                                            backgroundColor: item.isCity ? "#fafafa" : "#fff",
                                            zIndex: 2,
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            paddingY: 1,
                                            paddingLeft: item.isGrouped && filteredOptions.some(o => o.isCity && o.cityiata === item.cityiata) ? '35px' : '16px',
                                            borderBottom: '1px solid #f5f5f5',
                                        }}
                                    >
                                        {id === "flight" && item.isCity !== undefined ? (
                                            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                <div style={{ marginRight: '10px', marginTop: '4px', display: 'flex', alignItems: 'center' }}>
                                                    {item.isCity ? <LocationOnOutlinedIcon sx={{ color: '#555' }} /> : <FlightIcon sx={{ color: '#555', transform: 'rotate(45deg)' }} />}
                                                </div>
                                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                    <span style={{ fontSize: '15px', color: '#333' }}>
                                                        {item.isCity ? (
                                                            <>
                                                                <strong>{item.displayTitle.split(' ')[0]}</strong> {item.displayTitle.substring(item.displayTitle.indexOf(' ') + 1)}
                                                            </>
                                                        ) : (item.isGrouped && filteredOptions.some(o => o.isCity && o.cityiata === item.cityiata)) ? (
                                                            <>
                                                                <strong>{item.displayTitle.split(' ')[0]}</strong> {item.displayTitle.substring(item.displayTitle.indexOf(' ') + 1)}
                                                            </>
                                                        ) : (
                                                            <>
                                                                <strong>{item.rawAirportIata}</strong>
                                                                {(!item.isGrouped || !filteredOptions.some(o => o.isCity && o.cityiata === item.cityiata)) && item.rawCityName ? ` ${item.rawCityName} , ${item.rawCityIata} ` : ' '}
                                                                {item.rawAirportName}
                                                            </>
                                                        )}
                                                    </span>
                                                    {item.displaySubtitle && (
                                                        <span style={{ fontSize: '13px', color: '#777', marginTop: '2px' }}>
                                                            {item.displaySubtitle}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        ) : (
                                            item?.label
                                        )}
                                    </MenuItem>
                                ))
                            ) : (
                                <MenuItem>No Options</MenuItem>
                            )}
                        </Popper>
                    </ClickAwayListener>

                </Grid>
            </Grid>
        </>
    )
};
export default memo(CustomSearchField);